/**
 * blog_scheduler_controller.js — drag-and-drop blog publishing queue (Stimulus).
 *
 * Backs templates/admin/blog/scheduler.html (data-controller="blog-scheduler").
 * Two lists — Drafts and Queue — reorder by pointer drag. A drag can:
 *   - draft  -> queue : POST enqueue/<id>  (schedule it at the tail slot)
 *   - queue  -> draft : POST dequeue/<id>  (unschedule, back to draft)
 *   - queue  -> queue : POST reorder       (rewrite scheduled_at per new order)
 * The queue is a pure projection onto BlogPost.scheduled_at (BlogService owns
 * the slot math), so after any successful mutation we reload the page to render
 * the authoritative recomputed publish dates rather than guessing them here.
 *
 * Dragging uses Pointer Events, not HTML5 drag-and-drop: the real card is lifted
 * out of flow (position: fixed, fully opaque) and tracks the cursor as one solid
 * piece, while a dashed placeholder marks the drop slot. HTML5 DnD can't do this
 * — browsers force the drag image translucent. Pointer events also unify
 * mouse + touch. The drag is document-delegated so connect() owns one listener
 * set for the whole board and disconnect() removes it (Turbo-safe).
 */

import { Controller } from "@hotwired/stimulus";

var DRAG_THRESHOLD = 5;   // px of movement before a press becomes a drag
var FLIP_MS = 150;        // slide duration when siblings reflow around the placeholder

function csrfHeaders() {
    var token = (typeof FormValidator !== "undefined" && FormValidator.getCsrfToken)
        ? FormValidator.getCsrfToken() : null;
    return token ? { "X-CSRFToken": token } : {};
}

/**
 * The card the placeholder should sit before, given the pointer Y — the first
 * card whose vertical midpoint is below the pointer. null => append at the end.
 */
function cardBefore(list, y) {
    var cards = Array.prototype.slice.call(list.querySelectorAll(".scheduler-card"));
    for (var i = 0; i < cards.length; i++) {
        var box = cards[i].getBoundingClientRect();
        if (y < box.top + box.height / 2) return cards[i];
    }
    return null;
}

export default class extends Controller {
    static values = { reorderUrl: String };
    static targets = ["intervalFields", "weeklyFields", "configSummary", "configForm"];

    connect() {
        this.drag = null;         // active-drag state, or null when idle
        this._dragMoved = false;  // true once a press has become a real drag
        this._listeners = [
            [document, "pointerdown", this.onPointerDown.bind(this)],
            [document, "pointermove", this.onPointerMove.bind(this)],
            [document, "pointerup", this.onPointerUp.bind(this)],
            [document, "pointercancel", this.onPointerCancel.bind(this)],
            // Capture phase so we can cancel the click that fires after a drag
            // (grabbing a card by its title link must not also navigate to it).
            [document, "click", this.onClickCapture.bind(this), true],
        ];
        this._listeners.forEach(function (l) { l[0].addEventListener(l[1], l[2], l[3]); });
    }

    disconnect() {
        if (this.drag) this.abort();
        if (this._listeners) {
            this._listeners.forEach(function (l) { l[0].removeEventListener(l[1], l[2], l[3]); });
            this._listeners = null;
        }
    }

    onPointerDown(event) {
        this._dragMoved = false;  // any fresh press clears stale drag state
        if (event.button !== 0) return;  // primary button / touch only
        // Buttons keep their own click (Add to schedule); you never drag by them.
        // The title link is grabbable — a real drag suppresses its click below, so
        // the whole card (including its dominant title) is a drag handle.
        if (event.target.closest("button")) return;
        var card = event.target.closest(".scheduler-card");
        if (!card || !this.element.contains(card)) return;

        // Record the press; it only becomes a drag past the move threshold, so a
        // plain click on the title still navigates and a click elsewhere is inert.
        this.drag = {
            card: card,
            sourceList: card.closest("[data-list]").dataset.list,
            startX: event.clientX,
            startY: event.clientY,
            active: false,
            placeholder: null,
            offsetX: 0,
            offsetY: 0,
        };
    }

    onPointerMove(event) {
        var d = this.drag;
        if (!d) return;

        if (!d.active) {
            var moved = Math.hypot(event.clientX - d.startX, event.clientY - d.startY);
            if (moved < DRAG_THRESHOLD) return;
            this._dragMoved = true;  // this gesture is a drag, not a click
            this.lift(event);
        }

        event.preventDefault();  // stop text selection / scroll while dragging
        // Follow the cursor, keeping the grab point under the pointer.
        d.card.style.left = (event.clientX - d.offsetX) + "px";
        d.card.style.top = (event.clientY - d.offsetY) + "px";
        this.reposition(event);
    }

    /** Promote the press to an active drag: lift the card out of flow. */
    lift(event) {
        var d = this.drag;
        var card = d.card;
        var rect = card.getBoundingClientRect();

        var placeholder = document.createElement("div");
        placeholder.className = "scheduler-placeholder";
        placeholder.style.height = rect.height + "px";
        card.parentNode.insertBefore(placeholder, card);
        document.body.appendChild(card);  // remove from the list so it's out of flow

        d.placeholder = placeholder;
        d.offsetX = d.startX - rect.left;
        d.offsetY = d.startY - rect.top;
        d.active = true;

        card.classList.add("dragging");
        card.style.width = rect.width + "px";
        card.style.left = rect.left + "px";
        card.style.top = rect.top + "px";
    }

    /** Move the placeholder to the slot under the pointer, in whichever list. */
    reposition(event) {
        var d = this.drag;
        // The card has pointer-events:none while dragging, so elementFromPoint
        // reports what's underneath it — the list and its cards.
        var under = document.elementFromPoint(event.clientX, event.clientY);
        var list = under && under.closest ? under.closest("[data-list]") : null;
        if (!list || !this.element.contains(list)) return;

        var before = cardBefore(list, event.clientY);
        // Already in this slot — do nothing, so a pointermove that doesn't change
        // the order never restarts the slide animation.
        if (d.placeholder.parentNode === list && d.placeholder.nextElementSibling === before) return;

        var placeholder = d.placeholder;
        this.flipMove(function () {
            if (before) list.insertBefore(placeholder, before);
            else list.appendChild(placeholder);
        });
    }

    /**
     * Run a DOM mutation, then slide every displaced card from its old position
     * to its new one (FLIP): measure First rects, mutate, measure Last rects,
     * apply an inverting transform so each card still *looks* unmoved, force one
     * reflow, then transition the transform away so they glide into place. The
     * dragged card is excluded — it tracks the cursor via fixed positioning, not
     * flow — and cards that didn't move are skipped so nothing animates needlessly.
     */
    flipMove(mutate) {
        var cards = Array.prototype.slice
            .call(this.element.querySelectorAll(".scheduler-card"))
            .filter(function (c) { return !c.classList.contains("dragging"); });

        var first = cards.map(function (c) { return c.getBoundingClientRect(); });

        mutate();

        // Clear any in-flight transform so Last is the true post-layout position.
        cards.forEach(function (c) { c.style.transition = "none"; c.style.transform = ""; });
        var last = cards.map(function (c) { return c.getBoundingClientRect(); });

        var moved = [];
        cards.forEach(function (c, i) {
            var dx = first[i].left - last[i].left;
            var dy = first[i].top - last[i].top;
            if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
            c.style.transform = "translate(" + dx + "px, " + dy + "px)";  // Invert
            moved.push(c);
        });
        if (!moved.length) return;

        this.element.getBoundingClientRect();  // commit the inverted transforms
        moved.forEach(function (c) {           // Play: glide to the natural spot
            c.style.transition = "transform " + FLIP_MS + "ms ease";
            c.style.transform = "";
        });
    }

    onPointerUp() {
        var d = this.drag;
        if (!d) return;
        if (!d.active) { this.drag = null; return; }  // was a click, not a drag

        var list = d.placeholder.parentNode;
        var dest = list.dataset.list;
        var card = d.card;

        // Drop the solid card back into flow at the placeholder's slot.
        list.insertBefore(card, d.placeholder);
        d.placeholder.remove();
        card.classList.remove("dragging");
        card.style.cssText = "";

        this.drag = null;

        if (d.sourceList === "drafts" && dest === "queue") {
            this.post(card.dataset.enqueueUrl);
        } else if (d.sourceList === "queue" && dest === "drafts") {
            this.post(card.dataset.dequeueUrl);
        } else if (d.sourceList === "queue" && dest === "queue") {
            this.post(this.reorderUrlValue, { ordered_ids: this.queueOrder() });
        }
        // drafts -> drafts is a no-op (draft order is not persisted).
    }

    onPointerCancel() {
        if (this.drag) this.abort();
    }

    /**
     * A drag ends with a synthetic click on whatever is now under the cursor. If
     * the gesture was a real drag (moved past threshold), swallow that click so
     * grabbing a card by its title link doesn't also open the editor. A plain
     * click (no drag) never sets the flag, so navigation still works.
     */
    onClickCapture(event) {
        if (this._dragMoved) {
            this._dragMoved = false;
            event.preventDefault();
            event.stopPropagation();
        }
    }

    /** Restore the DOM without mutating anything (cancelled / disconnected mid-drag). */
    abort() {
        var d = this.drag;
        this.drag = null;
        if (!d || !d.active) return;
        d.placeholder.parentNode.insertBefore(d.card, d.placeholder);
        d.placeholder.remove();
        d.card.classList.remove("dragging");
        d.card.style.cssText = "";
    }

    /**
     * Cadence-mode radio changed: show the field group for the chosen mode
     * (interval count/unit vs weekday checkboxes). Initial visibility is
     * server-rendered from the saved config; this only handles live toggles.
     */
    modeChanged(event) {
        this.showModeFields(event.target.value === "weekly");
    }

    showModeFields(weekly) {
        this.intervalFieldsTarget.classList.toggle("d-none", weekly);
        this.weeklyFieldsTarget.classList.toggle("d-none", !weekly);
    }

    /**
     * The config card renders as a read-only summary of the SAVED schedule;
     * the form only appears on Edit. Saving posts and redirects, so the page
     * re-renders back in summary mode showing the confirmed setting.
     */
    editSchedule() {
        this.configSummaryTarget.classList.add("d-none");
        this.configFormTarget.classList.remove("d-none");
    }

    /**
     * Abandon edits: reset() restores the server-rendered attribute values
     * (i.e. the saved config), so a later Edit never shows stale unsaved
     * changes; the mode field groups are re-synced to the restored radio.
     */
    cancelScheduleEdit() {
        var form = this.configFormTarget;
        form.reset();
        var mode = form.querySelector('input[name="schedule_mode"]:checked');
        this.showModeFields(mode && mode.value === "weekly");
        form.classList.add("d-none");
        this.configSummaryTarget.classList.remove("d-none");
    }

    /** Button alternative to dragging a draft into the queue. */
    addToQueue(event) {
        var card = event.target.closest(".scheduler-card");
        if (card) this.post(card.dataset.enqueueUrl);
    }

    /** Menu alternative to dragging a queue card back to drafts (unschedule). */
    removeFromQueue(event) {
        var card = event.target.closest(".scheduler-card");
        if (card) this.post(card.dataset.dequeueUrl);
    }

    queueOrder() {
        var queue = this.element.querySelector('[data-list="queue"]');
        return Array.prototype.slice
            .call(queue.querySelectorAll(".scheduler-card"))
            .map(function (c) { return parseInt(c.dataset.postId, 10); });
    }

    /**
     * POST the mutation, then reload so the queue re-renders with the server's
     * authoritative scheduled_at dates. On failure, reload too — that reverts the
     * optimistic DOM move back to the server's truth.
     */
    async post(url, body) {
        var opts = {
            method: "POST",
            headers: csrfHeaders(),
            credentials: "same-origin",
        };
        if (body) {
            opts.headers = Object.assign({ "Content-Type": "application/json" }, opts.headers);
            opts.body = JSON.stringify(body);
        }
        try {
            var resp = await fetch(url, opts);
            if (!resp.ok) console.warn("[blog-scheduler] mutation failed:", resp.status);
        } catch (err) {
            console.error("[blog-scheduler] mutation error:", err);
        }
        this.reload();
    }

    reload() {
        if (window.Turbo && window.Turbo.visit) {
            window.Turbo.visit(window.location.href, { action: "replace" });
        } else {
            window.location.reload();
        }
    }
}
